import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { RouteTransitionOutlet } from './RouteTransitionOutlet';
import { Button, Modal, Skeleton, Toast } from '../primitives';
import { supabase } from '../../lib/supabase';
import { PenroseShell } from './PenroseShell';
import { PenroseTopHeader } from './PenroseTopHeader';
import { PenroseLeftRail } from './PenroseLeftRail';
import { AgentGlobalSidebar } from '../agent/AgentGlobalSidebar';
import { isIDE } from '../../lib/isIDE';
import { IdentityFlyout } from './IdentityFlyout';
import { RevocationError } from './RevocationError';
import { DiagnosticsModal } from './DiagnosticsModal';
import { SyncDiagnosticsAlerts } from './SyncDiagnosticsAlerts';
import { useShellSession, type ShellMembership } from './useShellSession';
import { useOnline } from './useOnline';
import { isRailSurface } from './surfaces';
import { useClearStaleOpenItem } from '../../hooks/useClearStaleOpenItem';
import { useOrgSwitcherVM } from '../../lib/vm/useOrgSwitcherVM';
import { useCreateOrgVM } from '../../lib/vm/useCreateOrgVM';
import { ActiveOrgProvider } from '../../lib/activeOrg';
import { useSyncDiagnosticsVM } from '../../lib/vm/useSyncDiagnosticsVM';
import {
  getUnsavedDrafts,
  type UnsavedDraft
} from '../../lib/registry/unsavedDraftsRegistry';

/** Key in sessionStorage. /invite sets this immediately before navigating
 *  to /dashboard so the shell can land the user in the just-accepted org
 *  without a server round-trip. One-shot — the shell consumes + clears. */
const ACTIVE_ORG_HINT_KEY = 'pn:active-org-hint';

/** 17.3b.2 — `<Outlet context>` surface exposed by `SignedInShell` so the
 *  dashboard's `<InvitationRow>` can fire the org-switcher VM's
 *  accept/decline actions without re-mounting the VM at the page level. */
export interface SignedInShellOutletContext {
  acceptInvitation: (token: string) => void;
  declineInvitation: (invitationId: string) => void;
  /** Active org id resolved by the shell. `null` while the session is still
   *  loading or before the user has picked their initial org. Paywall reads
   *  this so it can render the suspended org's name + Owner contact without
   *  re-resolving the active org itself. */
  activeOrgId: string | null;
  /** Display name for the active org — same source as the header chrome. */
  activeOrgName: string | null;
  /** Programmatically open the identity flyout (org switcher overlay). The
   *  `/paywall` page's `Open org switcher` button calls this. */
  openIdentityFlyout: () => void;
  /** Signs the user out + redirects to `/sign-in`. Same flow as the flyout's
   *  `Sign out` row. Exposed so the `/paywall` page can offer Sign out without
   *  re-implementing the supabase call. */
  signOut: () => Promise<void>;
  /** Create a new org via the shell's existing create+switch loop. Delegates
   *  to the shell's `handleCreateOrg` so there is a single VM instance. */
  createOrg: (name: string) => Promise<{ ok: true } | { ok: false; error: string }>;
  /** Switch to an org via the shell's existing `beginSwitch` workflow. */
  switchOrg: (orgId: string, orgName: string) => void;
}

/**
 * The signed-in chrome — composes `<PenroseShell>` + `<PenroseTopHeader>` +
 * `<PenroseLeftRail>` and re-hosts the legacy flyout / diagnostics / toast as
 * overlays so the multi-org switch / connectivity diagnostics / org-switch
 * toast behaviors carry forward. Wraps protected routes via react-router's
 * `<Outlet />`. Renders a skeleton first-paint while the session resolves;
 * on unauth, redirects to /sign-in?next=<path>.
 *
 * Active org is per-tab React state. On mount, the shell picks (in order):
 *   1. The org id stashed in sessionStorage by /invite or /password-reset
 *      after a multi-org pick (one-shot hint).
 *   2. The user's oldest membership (the "home org" — for non-invited
 *      sign-ups this is the org they just created).
 * A toast fires on session start + every org switch to make the current
 * org visible.
 */
export function SignedInShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const session = useShellSession();
  const online = useOnline();
  // 19.1 — mount the sync-diagnostics VM. Owns the session-only ring buffer
  // of Supabase fetches + installs the window.fetch interceptor. No view
  // wiring this turn; the modal overlay + banners land in 19.2 + 19.3.
  useSyncDiagnosticsVM();

  const [activeOrgId, setActiveOrgId] = useState<string | null>(null);
  const [toastOrgName, setToastOrgName] = useState<string | null>(null);
  const [createdOrgToast, setCreatedOrgToast] = useState<string | null>(null);
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [diagOpen, setDiagOpen] = useState(false);
  const [pendingSwitch, setPendingSwitch] = useState<{
    orgId: string;
    drafts: ReadonlyArray<UnsavedDraft>;
  } | null>(null);

  // Track memberships in a ref so the org-switcher's `navigate` callback can
  // read the latest `suspended_at` after `refreshSession()` settles without
  // capturing a stale closure value. The shell's session updates memberships
  // synchronously when `refresh()` resolves; reading them off the ref makes
  // the post-switch route decision deterministic.
  const membershipsRef = useRef(session.memberships);
  useEffect(() => {
    membershipsRef.current = session.memberships;
  }, [session.memberships]);

  // Mirror the active org into a ref so the cross-tab broadcast listener can
  // compare against the current value without resubscribing on every switch.
  const activeOrgIdRef = useRef(activeOrgId);
  activeOrgIdRef.current = activeOrgId;
  // Same for the session refresh fn, so the listener can refetch memberships
  // (e.g. after a cross-tab rename) without listing it as an effect dep.
  const refreshSessionRef = useRef(session.refresh);
  refreshSessionRef.current = session.refresh;

  // Track which org id the latest `beginSwitch` is heading toward so the
  // navigate callback below knows which membership to inspect. (The org id is
  // available on `onActiveOrgChange` before navigate fires; we just need to
  // pass it from one callback to the other.)
  const pendingSwitchOrgIdRef = useRef<string | null>(null);

  // Pick the initial active org once memberships have loaded.
  useEffect(() => {
    if (session.status !== 'authed') return;
    if (activeOrgId !== null) return;
    if (session.memberships.length === 0) return;

    const hint = (() => {
      try {
        return window.sessionStorage.getItem(ACTIVE_ORG_HINT_KEY);
      } catch {
        return null;
      }
    })();

    const fromHint = hint
      ? session.memberships.find((m) => m.org_id === hint)
      : null;
    const initial = fromHint ?? session.memberships[0];

    if (hint) {
      try {
        window.sessionStorage.removeItem(ACTIVE_ORG_HINT_KEY);
      } catch {
        /* sessionStorage unavailable — no-op */
      }
    }

    setActiveOrgId(initial.org_id);
    setToastOrgName(initial.org_name);
  }, [session.status, session.memberships, activeOrgId]);

  // Redirect on unauth — preserves the path the user tried to reach so they
  // can be sent back after re-sign-in (cluster-7).
  useEffect(() => {
    if (session.status === 'unauth') {
      const path = location.pathname + location.search;
      const next = encodeURIComponent(path);
      navigate(`/sign-in?next=${next}`, { replace: true });
    }
  }, [session.status, location.pathname, location.search, navigate]);

  // Cross-tab org switch. When another tab switches orgs it posts `switched`
  // on the shared channel. The shell is the single source of truth for the
  // active org, so we adopt it here once — every surface follows via
  // `ActiveOrgProvider`/`useActiveOrg`. We do NOT navigate (the other tab
  // stays where it is) and we guard against our own outbound message.
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    let channel: BroadcastChannel;
    try {
      channel = new BroadcastChannel('penrose-org-changed');
    } catch {
      return;
    }
    channel.onmessage = (event: MessageEvent) => {
      const msg = event.data as
        | { kind?: string; orgId?: string; name?: string | null }
        | null;
      if (!msg || typeof msg.orgId !== 'string') return;
      if (msg.kind === 'switched') {
        // Our own switch already set this locally — skip the echo.
        if (msg.orgId === activeOrgIdRef.current) return;
        const target = membershipsRef.current.find(
          (m) => m.org_id === msg.orgId
        );
        // Unknown org (e.g. just created in another tab, not yet in our
        // membership list) — ignore; our own refetch will pick it up.
        if (!target) return;
        setActiveOrgId(msg.orgId);
        setToastOrgName(msg.name ?? target.org_name);
      } else if (msg.kind === 'renamed' && msg.orgId === activeOrgIdRef.current) {
        setToastOrgName(msg.name ?? null);
        // Refetch memberships so the renamed org's name propagates to every
        // surface (header + dashboard, via the shell-derived activeOrgName).
        void refreshSessionRef.current?.();
      }
    };
    return () => channel.close();
  }, []);

  // Persist last-active rail surface to profiles.active_surface whenever the
  // route flips to a rail surface.
  useEffect(() => {
    if (!session.userId) return;
    const key = isRailSurface(location.pathname);
    if (!key) return;
    const uid = session.userId;
    (async () => {
      await supabase.from('profiles').update({ active_surface: key }).eq('id', uid);
    })();
  }, [location.pathname, session.userId]);

  // Keep the agent's active target ("Acting on" chip + per-turn context overlay)
  // reflecting what is CURRENTLY open: clear the open-item store when the route
  // is not an item editor, and when the active org changes (so a prior org's
  // item never leaks as the target after a switch).
  useClearStaleOpenItem(location.pathname, activeOrgId);

  function handlePickOrg(m: ShellMembership) {
    setFlyoutOpen(false);
    if (m.org_id === activeOrgId) return;
    // 17.2b — Check the unsaved-drafts registry. If any document-editor /
    // task-detail draft is dirty, gate the switch behind a confirm modal;
    // otherwise fire the VM's `beginSwitch` workflow immediately.
    const drafts = getUnsavedDrafts();
    if (drafts.length > 0) {
      setPendingSwitch({ orgId: m.org_id, drafts });
      return;
    }
    // Delegate the workflow (skeleton + refetch + navigate + broadcast) to
    // the VM. The header chrome + toast update synchronously via the
    // `onActiveOrgChange` callback wired below.
    void orgSwitcher.actions.beginSwitch(m.org_id);
  }

  function handleConfirmDiscardAndSwitch() {
    if (!pendingSwitch) return;
    const orgId = pendingSwitch.orgId;
    setPendingSwitch(null);
    void orgSwitcher.actions.beginSwitch(orgId);
  }

  function handleStay() {
    setPendingSwitch(null);
  }

  const handleSignOut = useCallback(async () => {
    setFlyoutOpen(false);
    await supabase.auth.signOut();
    navigate('/sign-in', { replace: true });
  }, [navigate]);

  const openIdentityFlyout = useCallback(() => {
    setFlyoutOpen(true);
  }, []);

  function handleAccount() {
    setFlyoutOpen(false);
    navigate('/account');
  }

  const orgSwitcher = useOrgSwitcherVM(
    {
      memberships: session.memberships,
      status: session.status,
      activeOrgId,
      viewerEmail: session.email
    },
    {
      onActiveOrgChange: (orgId, orgName) => {
        setActiveOrgId(orgId);
        setToastOrgName(orgName);
        // Remember which org id the in-flight switch is heading toward so the
        // post-`refreshSession` `navigate` callback below can decide whether
        // to override `/dashboard` with `/paywall` (suspension cluster 5C).
        pendingSwitchOrgIdRef.current = orgId;
      },
      refreshSession: session.refresh,
      navigate: (path) => {
        // Switching INTO a suspended org overrides the default `/dashboard`
        // destination with `/paywall`. Owners are never suspended (the schema
        // forbids it), so this branch only fires for Members/Admins.
        //
        // TODO(billing-canceled-cascade): when an Owner of a canceled-cascade
        // org switches in, they need a `/dashboard` banner ("Subscription
        // canceled. Suspended members: N. Manage billing in web app →")
        // surfaced by a separate feature folder. Add the banner-mount here
        // (or in `Dashboard.tsx`) when that work lands; for now Owners land on
        // `/dashboard` normally.
        const targetOrgId = pendingSwitchOrgIdRef.current;
        pendingSwitchOrgIdRef.current = null;
        if (path === '/dashboard' && targetOrgId) {
          const target = membershipsRef.current.find(
            (m) => m.org_id === targetOrgId
          );
          if (target && target.suspended_at !== null) {
            navigate('/paywall');
            return;
          }
        }
        navigate(path);
      },
      broadcast: (message) => {
        if (typeof BroadcastChannel === 'undefined') return;
        try {
          const ch = new BroadcastChannel('penrose-org-changed');
          ch.postMessage(message);
          ch.close();
        } catch {
          /* BroadcastChannel unavailable — in-tab switch still applied. */
        }
      },
      subscribeBroadcast: (listener) => {
        if (typeof BroadcastChannel === 'undefined') return () => {};
        let ch: BroadcastChannel;
        try {
          ch = new BroadcastChannel('penrose-org-changed');
        } catch {
          return () => {};
        }
        const onMessage = (e: MessageEvent) => listener(e.data);
        ch.addEventListener('message', onMessage);
        return () => {
          ch.removeEventListener('message', onMessage);
          ch.close();
        };
      }
    }
  );

  const createOrg = useCreateOrgVM();

  // Stable ref mirrors so handleCreateOrg doesn't need to list the VM
  // objects as deps (they're stable useCallback values but the linter can't
  // know that without seeing the source).
  const createOrgSubmitRef = useRef(createOrg.submit);
  createOrgSubmitRef.current = createOrg.submit;
  const beginSwitchRef2 = useRef(orgSwitcher.actions.beginSwitch);
  beginSwitchRef2.current = orgSwitcher.actions.beginSwitch;

  const handleCreateOrg = useCallback(
    async (name: string): Promise<{ ok: true } | { ok: false; error: string }> => {
      const result = await createOrgSubmitRef.current(name);
      if (!result.ok) {
        return { ok: false, error: result.error.message };
      }
      // Surface the toast and kick off the switch into the new org, then
      // return immediately so the inline form closes without waiting for the
      // switch's membership refetch. beginSwitch is fire-and-forget here to
      // match the other switch call sites in this file.
      setCreatedOrgToast(result.name);
      void beginSwitchRef2.current(result.org_id, result.name);
      return { ok: true };
    },
    []
  );

  const active = orgSwitcher.derived.active;
  const isRevoked = orgSwitcher.derived.isRevoked;

  const loading = session.status === 'loading';
  const displayName = session.profile?.display_name ?? null;
  // 17.4b — when the active org's membership has been revoked, header chrome
  // pins the *last-known* org name + a `…` role chip until the user picks a
  // remaining org from the embedded picker.
  const activeOrgName = isRevoked
    ? orgSwitcher.derived.revokedOrgName
    : active?.org_name ?? null;
  const activeRoleName = isRevoked ? '…' : active?.function_role_name ?? null;

  const handleSwitchOrg = useCallback((orgId: string, orgName: string) => {
    void beginSwitchRef2.current(orgId, orgName);
  }, []);

  // Don't render anything in the unauth flash before the redirect fires.
  if (session.status === 'unauth') return null;

  const handleAcceptInvitation = (token: string) => {
    setFlyoutOpen(false);
    void orgSwitcher.actions.acceptInvitation(token);
  };

  const handleDeclineInvitation = (invitationId: string) => {
    void orgSwitcher.actions.declineInvitation(invitationId);
  };

  const flyout =
    flyoutOpen && !loading ? (
      <IdentityFlyout
        activeOrgId={activeOrgId}
        memberships={orgSwitcher.derived.sorted}
        onPickOrg={handlePickOrg}
        onAccount={handleAccount}
        onSignOut={handleSignOut}
        onClose={() => setFlyoutOpen(false)}
        pendingInvitations={orgSwitcher.state.pendingInvitations}
        onAcceptInvitation={handleAcceptInvitation}
        onDeclineInvitation={handleDeclineInvitation}
        online={online}
        onDiagnosticsClick={() => setDiagOpen(true)}
        onCreateOrg={handleCreateOrg}
        createOrgDisabled={
          createOrg.state === 'creating' ||
          orgSwitcher.state.switchPhase === 'switching'
        }
      />
    ) : null;

  // 17.3b.2 — Expose the pending-invitations action surface to the
  // dashboard's `<InvitationRow>` via `<Outlet>` context. Lighter-weight
  // than a dedicated module-scoped context; the dashboard is the only
  // current consumer.
  const outletContext: SignedInShellOutletContext = {
    acceptInvitation: handleAcceptInvitation,
    declineInvitation: handleDeclineInvitation,
    activeOrgId,
    activeOrgName,
    openIdentityFlyout,
    signOut: handleSignOut,
    createOrg: handleCreateOrg,
    switchOrg: handleSwitchOrg
  };

  return (
    <ActiveOrgProvider activeOrgId={activeOrgId} activeOrgName={activeOrgName}>
    <div
      data-testid="signed-in-shell"
      data-shell-status={session.status}
      data-active-surface={isRailSurface(location.pathname) ?? ''}
    >
      <PenroseShell
        sidebar={isIDE() ? <AgentGlobalSidebar /> : undefined}
        header={
          <PenroseTopHeader
            scope={
              loading ? (
                <Skeleton width={96} height={14} />
              ) : activeOrgName ? (
                <span data-testid="header-scope">{activeOrgName}</span>
              ) : (
                <span data-testid="header-scope-empty">No active org</span>
              )
            }
            identityName={displayName ?? '…'}
            identityRole={activeRoleName ?? undefined}
            identityExpanded={flyoutOpen}
            onIdentityClick={() => setFlyoutOpen((v) => !v)}
            identityFlyout={flyout}
          />
        }
        rail={
          <PenroseLeftRail onSignOut={handleSignOut} />
        }
        main={
          <div className="pn-shell-main" data-testid="shell-main">
            {loading ? (
              <div className="pn-surface-stub" data-testid="shell-main-skeleton">
                <Skeleton width="40%" height={32} />
                <Skeleton width="60%" height={14} />
                <Skeleton width="55%" height={14} />
              </div>
            ) : isRevoked &&
              orgSwitcher.state.switchPhase === 'switching' ? (
              // Embedded-picker click during a revocation: replace the
              // revocation surface with the switching skeleton until the
              // post-switch navigate lands. `beginSwitch` clears revocation
              // on success so the next render unmounts both.
              <div
                className="pn-surface-stub pn-shell-switching"
                data-testid="shell-main-switching"
                aria-busy="true"
              >
                <Skeleton width="40%" height={32} />
                <Skeleton width="60%" height={14} />
                <Skeleton width="55%" height={14} />
              </div>
            ) : isRevoked ? (
              <RevocationError
                revokedOrgName={orgSwitcher.derived.revokedOrgName ?? ''}
                // Filter the revoked org out — the detector path's refetch
                // already drops it from `memberships`, but the eager
                // `markRevoked` path leaves it in place.
                memberships={orgSwitcher.derived.sorted.filter(
                  (m) =>
                    m.org_id !== orgSwitcher.state.revocation?.revokedOrgId
                )}
                pendingInvitations={orgSwitcher.state.pendingInvitations}
                onPickOrg={(orgId) =>
                  void orgSwitcher.actions.beginSwitch(orgId)
                }
                onAcceptInvitation={handleAcceptInvitation}
                onDeclineInvitation={handleDeclineInvitation}
                onSignOut={handleSignOut}
              />
            ) : (
              <>
                <RouteTransitionOutlet context={outletContext} />
                {orgSwitcher.state.switchPhase === 'switching' ? (
                  <div
                    className="pn-surface-stub pn-shell-switching"
                    data-testid="shell-main-switching"
                    aria-busy="true"
                  >
                    <Skeleton width="40%" height={32} />
                    <Skeleton width="60%" height={14} />
                    <Skeleton width="55%" height={14} />
                  </div>
                ) : null}
              </>
            )}
          </div>
        }
      />
      <DiagnosticsModal
        open={diagOpen}
        onClose={() => setDiagOpen(false)}
        viewerId={session.userId}
        activeOrgId={activeOrgId}
      />
      <SyncDiagnosticsAlerts activeOrgId={activeOrgId} />
      {toastOrgName ? (
        <Toast title="Working in" onDismiss={() => setToastOrgName(null)}>
          {toastOrgName}
        </Toast>
      ) : null}
      {createdOrgToast ? (
        <Toast
          title="Created"
          onDismiss={() => setCreatedOrgToast(null)}
          data-testid="created-org-toast"
        >
          <strong>{createdOrgToast}</strong>. You're in.
        </Toast>
      ) : null}
      <Modal
        open={pendingSwitch !== null}
        onClose={handleStay}
        title="Discard unsaved changes and switch?"
        dataTestId="org-switch-confirm"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={handleStay}
              data-testid="org-switch-confirm-stay"
            >
              Stay
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmDiscardAndSwitch}
              data-testid="org-switch-confirm-discard"
            >
              Discard &amp; switch
            </Button>
          </>
        }
      >
        <p>You have unsaved edits in:</p>
        <ul
          className="pn-org-switch-drafts"
          data-testid="org-switch-confirm-drafts"
        >
          {pendingSwitch?.drafts.map((d) => (
            <li key={d.id} data-testid="org-switch-confirm-draft">
              {d.label}
            </li>
          ))}
        </ul>
        <p>Switching will discard these drafts (server state untouched).</p>
      </Modal>
    </div>
    </ActiveOrgProvider>
  );
}
